import dayjs from 'dayjs';
import { Communication, Task } from '@medplum/fhirtypes';
import { isReferenceOf } from './services';

/**
 * Medplum _filter expression for inbox items whose focus is not a Communication
 * (clinical/work tasks vs SMS, calls, chat, notes, fax notifications).
 */
// Note: Medplum's _filter language supports `pr true` (present) but does not
// reliably accept the `missing` keyword in all contexts (e.g. GraphQL TaskList).
// To filter for "missing", we negate the "present" expression instead.
export const INBOX_CLINICAL_TASKS_FILTER = 'not (focus:Communication.category pr true)';

type CommunicationLike = {
  resourceType?: string;
  status?: unknown;
  payload?: unknown;
};

export function isCommunicationFocusResource(resource: unknown): resource is Communication {
  if (!resource || typeof resource !== 'object') {
    return false;
  }

  const record = resource as CommunicationLike;
  if (record.resourceType !== undefined && record.resourceType !== 'Communication') {
    return false;
  }

  return 'status' in record && 'payload' in record;
}

/**
 * Whether an inbox Task is a clinical/work task (not a Communication-backed notification).
 */
export function isInboxClinicalTask(task: Pick<Task, 'focus'>, sourceCommunication?: unknown): boolean {
  if (isCommunicationFocusResource(sourceCommunication)) {
    return false;
  }

  if (isReferenceOf(task.focus, 'Communication')) {
    return false;
  }

  return true;
}

/**
 * The slice of an inbox row the due-date helpers read. `InboxSortableNotification`
 * (inbox-sort.ts) extends it, so the sort and the future filter share one contract.
 */
export interface InboxFutureFilterableNotification {
  task: Pick<Task, 'focus' | 'executionPeriod'>;
  sourceCommunication?: unknown;
}

/**
 * A clinical task whose due date (`executionPeriod.end`) falls after today,
 * at day granularity in the viewer's local time zone (same rule as the
 * due-date sort buckets). Undated tasks and Communication-backed
 * notifications are never "future": their `executionPeriod` is a priority
 * window, not a due date.
 */
export function isFutureInboxTask(item: InboxFutureFilterableNotification, now: Date = new Date()): boolean {
  if (!isInboxClinicalTask(item.task, item.sourceCommunication)) {
    return false;
  }

  const end = item.task.executionPeriod?.end;
  if (!end) {
    return false;
  }

  return dayjs(end).isAfter(now, 'day');
}

/** Inbox default (BAR-2679): only tasks due today or overdue; future ones are opt-in. */
export function hideFutureInboxTasks<T extends InboxFutureFilterableNotification>(
  items: T[],
  now: Date = new Date()
): T[] {
  return items.filter((item) => !isFutureInboxTask(item, now));
}
